import type { ReactNode } from 'react';

import { useIslandSection } from './magic-island';
import type { IslandMode } from './magic-island';

const SUPPRESS_ISLAND: IslandMode = { kind: 'hidden' };

/**
 * Blanks the Magic Island across a page's whole closing run.
 *
 * Past the last item-related section the page has stopped talking about the
 * item, so the enquiry pill has nothing left to act on — it would float the
 * page's previous ask over the ones that replaced it, Get Matched and the
 * account band.
 *
 * The claim covers the run rather than each block inside it. The island falls
 * back to the enquiry pill whenever no section holds the reading line, and the
 * spacers between the closing's blocks hold nothing — so suppressing block by
 * block flashes the pill back on in each gap. One claim over the whole region
 * keeps it down until the site footer, which the island already binds against.
 *
 * `mode` replaces what that one claim says. A page whose closing has something
 * of its own to offer, the next product for one, hands it over here rather
 * than registering a second claim on a block inside the run: a block is a few
 * hundred pixels tall against a reading line a few dozen pixels tall, so its
 * claim holds for a fraction of the run and the gaps either side fall straight
 * through to the enquiry pill.
 */
export const IslandFreeClosing = ({
    children,
    className,
    mode,
    priority,
}: {
    children: ReactNode;
    className?: string;
    /** What the run claims. Defaults to blanking the island. */
    mode?: IslandMode | null;
    /**
     * Raised when this run has to outrank claims made inside it. A run that
     * covers the page's carousels contains their pagination claims, and with
     * equal priority the innermost element wins for as long as it holds the
     * reading line.
     */
    priority?: number;
}) => {
    const suppressRef = useIslandSection<HTMLDivElement>(
        mode ?? SUPPRESS_ISLAND,
        { priority },
    );
    return (
        <div ref={suppressRef} className={className}>
            {children}
        </div>
    );
};
